<template>
  <div class="card">
    <div style="background: #DBE7F2" class="p-2">
      <TitleButton :showBtn="false" :showAddNew="false" title="Narrative Report"/>
      <div class="row match-height mt-1">
        <div class="col-12 col-sm-6 col-md-4 col-lg-4">
          <label for="colFormLabel" >Select Project</label>
          <v-select
            placeholder="Select Project"
            :options="projects"
            label="name"
            :reduce="name => name.id"
            v-model="projectId"
            @option:selected="onSelectProject"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-4 col-lg-3" v-if="projectId">
          <label for="colFormLabel">Team Member</label>
          <v-select
            placeholder="Select Team Member"
            v-model="teamMemberId"
            :options="teamMembers"
            label="team_member_name"
            :reduce="name => name.member.id"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel">Budget</label>
          <v-select
            placeholder="Select Budget"
            v-model="budgetMasterId"
            :options="donors"
            label="name"
            :reduce="name => name.id"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-4 col-lg-2">
          <label class="colFormLabel">Interval</label>
          <v-select
              placeholder="Select Interval"
              v-model="interval"
              :options="intervals"
              label="name"
              :reduce="name => name.id"
          />
        </div>
      </div>
      <DateQuerySetter btnTitle="Go" @onClickGo="getReportData"/>
      <div class="d-flex justify-content-end mt-2">
        <button 
          @click="exportReport('narrative-report')"
          class="btn btn-sm btn-outline-secondary">
            Export
        </button>
      </div>
    </div>
    <Loader v-if="isLoading" />

    <!-- TABLE -->
    <div v-if="projectAccounts.length" class="mt-3">
      <div class="table-responsive">
        <table class="table table-bordered table-striped">

          <thead class="text-center">
            <!-- First row: main sections -->
            <tr>
              <th rowspan="2">Activity Group</th>
              <th colspan="4">TOTAL</th>
              <th colspan="5">CURRENT PERIOD</th>
            </tr>
           
            <tr>
              <!-- TOTAL columns -->
              <th class="text-end">Total Target</th>
              <th class="text-end">Total Achievement</th>
              <th class="text-end">Balance</th>
              <th class="text-end">Total Achievement %</th>
            
              <!-- CURRENT PERIOD columns -->
              <th class="text-end">Current Target</th>
              <th class="text-end">Current Achievement</th>
              <th class="text-end">Current Balance</th>
              <th class="text-end">Current Achievement %</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="item in projectAccounts" :key="item.id" :class="item.level === 0 ? 'font-bold' : ''">
              <td :style="{ paddingLeft: (item.level * 20) + 'px !important' }">
                {{ item.name }}
              </td>
              <td class="text-end">{{ item.total_target }}</td>
              <td class="text-end">{{ item.total_achievement }}</td>
              <td class="text-end">{{ item.total_target - item.total_achievement }}</td>
              <td class="text-end">{{ item.total_target > 0 ? ((item.total_achievement / item.total_target) * 100).toFixed(2) : '0.00' }}%</td>
              <td class="text-end">{{ commaFormat(item.current_target) }}</td>
              <td class="text-end">{{ commaFormat(item.current_achievement) }}</td>
              <td class="text-end">{{ commaFormat(item.current_target - item.current_achievement) }}</td>
              <td class="text-end">{{ item.current_target > 0 ? ((item.current_achievement / item.current_target) * 100).toFixed(2) : '0.00' }}%</td>
            </tr>
            <tr class="font-bold">
              <td>Grand Total</td>
              <td class="text-end">{{ grandTotal.total_target }}</td>
              <td class="text-end">{{ grandTotal.total_achievement }}</td>
              <td class="text-end">{{ grandTotal.total_balance }}</td>
              <td class="text-end">{{ grandTotal.total_percent }}%</td>
              <td class="text-end">{{ commaFormat(grandTotal.current_target) }}</td>
              <td class="text-end">{{ commaFormat(grandTotal.current_achievement) }}</td>
              <td class="text-end">{{ commaFormat(grandTotal.current_balance) }}</td>
              <td class="text-end">{{ grandTotal.current_percent }}%</td>
            </tr>
          </tbody>

        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, inject, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import TitleButton from '@/components/atom/TitleButton'
import DateQuerySetter from '@/components/atom/DateQuerySetter'
import Loader from '@/components/atom/LoaderComponent'
import handleProjects from "@/services/modules/procurement/project";
import handleInventory from "@/services/modules/inventory";
import { commaFormat } from "@/services/utils/global";

const {fetchProjects, fetchProjectDonors, fetchNarrativeReport, fetchProjectTeams} = handleProjects();
const {fetchHome} = handleInventory();

const $route = useRoute();
const showError = inject('showError');
const token = localStorage.getItem('token');
const projects = ref([]);
const donors = ref([]);
const projectAccounts = ref([]);
const response = ref({});

const projectId = ref(null);
const teamMemberId = ref(null);
const budgetMasterId = ref(null);
const interval = ref(null);
const teamMembers = ref([]);
const isLoading = ref(false);
const homeContent = ref({});

//computed
const intervals = computed(() => {
  let intervals = [];
  if(homeContent.value.hasOwnProperty('project_intervals')) {
    Object.keys(homeContent.value.project_intervals).forEach( id => {
      intervals.push({
        id: id,
        name: homeContent.value.project_intervals[id],
      })
    })
  }
  return intervals;
})

const grandTotal = computed(() => {
  let totalTarget = 0
  let totalAchievement = 0
  let currentTarget = 0
  let currentAchievement = 0
  
  projectAccounts.value.forEach(item => {
    totalTarget += Number(item.total_target) || 0
    totalAchievement += Number(item.total_achievement) || 0
    currentTarget += Number(item.current_target) || 0
    currentAchievement += Number(item.current_achievement) || 0
  })
  
  return {
    total_target: totalTarget,
    total_achievement: totalAchievement,
    total_balance: totalTarget - totalAchievement,
    total_percent: totalTarget > 0 ? ((totalAchievement / totalTarget) * 100).toFixed(2) : '0.00',
    current_target: currentTarget,
    current_achievement: currentAchievement,
    current_balance: currentTarget - currentAchievement,
    current_percent: currentTarget > 0 ? ((currentAchievement / currentTarget) * 100).toFixed(2) : '0.00'
  }
})

const query = `?company_id=${$route.params.companyId}`;

const onSelectProject = async () => {
  isLoading.value = true;
  donors.value = [];
  teamMembers.value = [];
  teamMemberId.value = null;
  try {
    const donorRes = await fetchProjectDonors(projectId.value, query);
    if(donorRes.status) donors.value = donorRes.data;

    const teamRes = await fetchProjectTeams(projectId.value, query);
    if(teamRes.status) teamMembers.value = teamRes.data;
  } catch (err) {
    showError('Something went wrong');
  } finally {
    isLoading.value = false;
  }
};

const exportReport = (type= 'narrative-report') => {
  if(projectId.value === null){
    showError('Please select a project');
    return;
  }
  if(budgetMasterId.value === null){
    showError('Please select a budget');
    return;
  }
  if(interval.value === null){
    showError('Please select an interval');
    return;
  }
  let objQuery = {
    company_id: $route.params.companyId,
    budget_master_id: budgetMasterId.value ?? '',
    start_date: $route.query.start,
    end_date: $route.query.end,
    interval: interval.value,
    team_member_id: teamMemberId.value || '',
    _token: token,
    report_type: "narrative-report",
    report_title: "Narrative Report"
  }
  let params = Object.entries(objQuery).map(([key, val]) => `${key}=${val}`).join('&');
  let url = `${process.env.VUE_APP_BASE_URL}/export/${type}/${projectId.value}?${params}`;
  let a = document.createElement('a');
  a.setAttribute('href', url);
  a.click();
};

const getReportData = async () => {
  if(!projectId.value || !budgetMasterId.value || !interval.value){
    showError('Please select project, budget and interval');
    return;
  }

  isLoading.value = true;

  try {
    const start = $route.query.start;
    const end = $route.query.end;

    const response = await fetchNarrativeReport(
      projectId.value,
      `?company_id=${$route.params.companyId}&budget_master_id=${budgetMasterId.value}&start_date=${start}&end_date=${end}&interval=${interval.value}&team_member_id=${teamMemberId.value || ''}`
    );

    projectAccounts.value = response.data.project_accounts || [];

  } catch (error) {
    showError('Something went wrong');
  } finally {
    isLoading.value = false;
  }
};

onMounted( async () => {
  isLoading.value = true;
  try {
    const homeRes = await fetchHome();
    if (homeRes.data) homeContent.value = homeRes.data;

    const projectRes = await fetchProjects(query);
    if (projectRes.status) projects.value = projectRes.data;

    const { project_id, budget_master_id, interval: interValue, team_member_id, start, end } = $route.query;
    
    if (project_id) {
      projectId.value = parseInt(project_id);
      await onSelectProject();
      
      if (team_member_id) teamMemberId.value = parseInt(team_member_id);
      if (budget_master_id) budgetMasterId.value = parseInt(budget_master_id);
      if (interValue) interval.value = interValue;
      
      if (start && end) {
        await getReportData();
      }
    }
  } catch (err) {
    showError('Failed to load initial data');
  } finally {
    isLoading.value = false;
  }
})

</script>

<style scoped>
.font-bold {
  font-weight: bold !important;
}
</style>

